import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PublicGrantCard } from "@/components/PublicGrantCard";
import { ATECO, REGIONI } from "@/lib/constants";
import {
  Sparkles, ArrowRight, Building2, Target, ShieldCheck, Euro, CheckCircle2, Search, Loader2, Zap,
} from "lucide-react";

const steps = [
  { icon: Building2, title: "Carica il profilo", desc: "Inserisci ATECO, regione, dimensione, fatturato e obiettivi della tua azienda." },
  { icon: Search, title: "L'AI cerca i bandi", desc: "Il motore analizza incentivi nazionali, regionali ed europei rilevanti per te." },
  { icon: Target, title: "Punteggio di match", desc: "Ricevi una lista di bandi con percentuale di compatibilità e analisi dettagliata." },
];

const feats = [
  { icon: Euro, title: "Fondo perduto & crediti d'imposta", desc: "Transizione 5.0, Nuova Sabatini, bandi FESR regionali e molto altro." },
  { icon: ShieldCheck, title: "Analisi di idoneità", desc: "Capisci subito perché la tua azienda rientra nei requisiti del bando." },
  { icon: CheckCircle2, title: "Preferiti & scadenze", desc: "Salva i bandi, monitora lo stato della domanda e non perdere le deadline." },
];

export default function Landing() {
  const [example, setExample] = useState(null);
  const [ateco, setAteco] = useState("");
  const [regione, setRegione] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [limitMsg, setLimitMsg] = useState("");

  useEffect(() => {
    api.get("/public/example").then(({ data }) => setExample(data.bando)).catch(() => {});
  }, []);

  const runQuickSearch = async () => {
    if (!ateco || !regione) return;
    setLoading(true);
    setLimitMsg("");
    setResults(null);
    try {
      const { data } = await api.post("/public/quick-search", { ateco, regione });
      setResults(data.bandi || []);
    } catch (err) {
      if (err.response?.status === 429) {
        setLimitMsg(formatApiErrorDetail(err.response.data?.detail) || "Limite prova raggiunto.");
      } else {
        setLimitMsg("Ricerca non riuscita, riprova tra poco.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <img src="/logo-atb.png" alt="A Tutto Bando" className="h-9 w-9 rounded-xl" />
          <span className="font-display text-lg font-bold tracking-tight">
            A Tutto<span className="text-indigo-600"> Bando</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" data-testid="landing-login-button">
            <Link to="/login">Accedi</Link>
          </Button>
          <Button asChild data-testid="landing-register-button">
            <Link to="/login?mode=register">Inizia gratis</Link>
          </Button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="grid-backdrop absolute inset-0 h-[520px]" />
        <div className="relative mx-auto max-w-4xl px-4 pb-12 pt-16 text-center sm:px-6 lg:pt-24">
          <div className="animate-in-up mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white px-4 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm">
            <ShieldCheck className="h-3.5 w-3.5" />
            Risultati verificabili: fonte ufficiale, scadenza e requisiti sempre in chiaro
          </div>
          <h1 className="animate-in-up font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "60ms" }}>
            Bandi verificabili
            <br />
            <span className="bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text text-transparent">
              per la tua azienda
            </span>
          </h1>
          <p className="animate-in-up mx-auto mt-6 max-w-2xl text-base text-slate-600 sm:text-lg" style={{ animationDelay: "120ms" }}>
            Non solo una percentuale: per ogni bando vedi la fonte ufficiale, la data dell'ultimo
            controllo, i requisiti già soddisfatti e quelli da verificare. Provalo subito, senza registrarti.
          </p>
        </div>

        {/* Prova rapida pubblica */}
        <div className="relative mx-auto max-w-3xl px-4 pb-4 sm:px-6">
          <div className="animate-in-up rounded-2xl border border-slate-200 bg-white p-5 shadow-lg sm:p-6" style={{ animationDelay: "160ms" }} data-testid="quick-search-widget">
            <div className="mb-3 flex items-center gap-2">
              <Zap className="h-5 w-5 text-indigo-600" />
              <h2 className="font-display text-lg font-bold">Prova rapida — senza registrazione</h2>
            </div>
            <p className="mb-4 text-sm text-slate-500">
              Inserisci codice ATECO e regione: ti mostriamo fino a 3 bandi di esempio con la spiegazione del match.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Select value={ateco} onValueChange={setAteco}>
                <SelectTrigger className="sm:flex-1" data-testid="quick-ateco-select"><SelectValue placeholder="Codice ATECO" /></SelectTrigger>
                <SelectContent>
                  {ATECO.map((a) => <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={regione} onValueChange={setRegione}>
                <SelectTrigger className="sm:flex-1" data-testid="quick-region-select"><SelectValue placeholder="Regione" /></SelectTrigger>
                <SelectContent>
                  {REGIONI.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={runQuickSearch} disabled={loading || !ateco || !regione} className="gap-2" data-testid="quick-search-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Prova
              </Button>
            </div>

            {limitMsg && (
              <div className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="quick-limit-message">
                {limitMsg}
              </div>
            )}

            {loading && (
              <div className="mt-5 flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Cerco bandi reali sul web...
              </div>
            )}

            {results && results.length > 0 && (
              <div className="mt-5 grid grid-cols-1 gap-4" data-testid="quick-results">
                {results.map((g) => <PublicGrantCard key={g.id} grant={g} />)}
                <div className="rounded-xl bg-slate-900 p-4 text-center">
                  <p className="text-sm text-slate-300">Registrati per ricerche complete, salvate e monitorate nel tempo.</p>
                  <Button asChild className="mt-3 gap-2 bg-white text-slate-900 hover:bg-slate-100">
                    <Link to="/login?mode=register">Crea account gratis <ArrowRight className="h-4 w-4" /></Link>
                  </Button>
                </div>
              </div>
            )}
            {results && results.length === 0 && (
              <p className="mt-4 text-sm text-slate-500">Nessun bando trovato per questa combinazione. Prova un altro ATECO o regione.</p>
            )}
          </div>
        </div>
      </section>

      {/* Scheda di esempio reale */}
      {example && (
        <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="mb-4 text-center">
            <p className="eyebrow text-indigo-600">Esempio reale</p>
            <h2 className="mt-1 font-display text-2xl font-bold sm:text-3xl">Ecco come appare un risultato</h2>
            <p className="mt-2 text-slate-500">Fonte, scadenza, requisiti confermati e da verificare: tutto in chiaro.</p>
          </div>
          <PublicGrantCard grant={example} />
        </section>
      )}

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
                <s.icon className="h-5 w-5" />
              </span>
              <div className="mt-4 font-mono text-xs text-slate-400">STEP {i + 1}</div>
              <h3 className="mt-1 font-display text-lg font-bold">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-500">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-200 bg-slate-900 p-8 sm:p-12">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
            Non lasciare soldi sul tavolo
          </h2>
          <p className="mt-2 max-w-2xl text-slate-300">
            Ogni anno vengono stanziati miliardi in incentivi per le imprese. A Tutto Bando
            ti aiuta a scoprire quelli adatti a te.
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {feats.map((f, i) => (
              <div key={i} className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
                <f.icon className="h-6 w-6 text-indigo-400" />
                <h3 className="mt-3 font-display text-base font-semibold text-white">{f.title}</h3>
                <p className="mt-1 text-sm text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>
          <Button asChild size="lg" className="mt-9 gap-2 bg-white text-slate-900 hover:bg-slate-100">
            <Link to="/login?mode=register">
              Inizia ora
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-400">
        A Tutto Bando · I risultati sono suggerimenti generati dall'AI, verifica sempre sui portali ufficiali.
      </footer>
    </div>
  );
}
